import React, { useState } from 'react';
import { QrCode, X, Search, AlertCircle, CheckCircle } from 'lucide-react';
import { BarcodeScanner } from './BarcodeScanner';
import { Building, Floor, Apartment } from '../types/building';

interface ApartmentQuickScanProps {
  building: Building;
  onClose: () => void;
  onApartmentFound: (apartment: Apartment, floorId: string) => void;
}

export const ApartmentQuickScan: React.FC<ApartmentQuickScanProps> = ({
  building,
  onClose,
  onApartmentFound
}) => {
  const [showScanner, setShowScanner] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResult, setSearchResult] = useState<{
    apartment: Apartment;
    floorId: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleScanComplete = (scannedValue: string) => {
    setShowScanner(false);
    setSearchTerm(scannedValue);
    searchApartment(scannedValue);
  };

  const searchApartment = (term: string) => {
    setError(null);
    setSearchResult(null);

    // Clean up the search term (remove spaces, etc.)
    const cleanTerm = term.trim();
    
    if (!cleanTerm) {
      setError('Bitte geben Sie eine Wohnungsnummer ein');
      return;
    }

    // Search for the apartment in the building
    for (const floor of building.floors) {
      for (const apartment of floor.apartments) {
        // Check if the formatted number matches
        if (apartment.formattedNumber === cleanTerm) {
          setSearchResult({ apartment, floorId: floor.id });
          return;
        }
      }
    }

    // If we get here, no apartment was found
    setError(`Keine Wohneinheit mit der Nummer "${cleanTerm}" gefunden`);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    searchApartment(searchTerm);
  };

  const handleSelectApartment = () => {
    if (searchResult) {
      onApartmentFound(searchResult.apartment, searchResult.floorId);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <QrCode className="w-6 h-6 text-blue-600" />
            <span>Wohneinheit schnell finden</span>
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
             <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          <p className="text-gray-600">
            Scannen Sie den Barcode/QR-Code einer Wohneinheit oder geben Sie die Wohnungsnummer manuell ein.
          </p>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="z.B. 000-001"
                className="w-full p-2.5 border border-gray-300 rounded-lg pr-10"
              />
              <button
                type="submit"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
              >
                <Search className="w-5 h-5" />
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowScanner(true)}
              className="p-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <QrCode className="w-5 h-5" />
            </button>
          </form>

          {error && (
            <div className="bg-red-50 p-4 rounded-lg flex items-start">
              <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          )}

          {searchResult && (
            <div className="bg-green-50 p-4 rounded-lg">
              <div className="flex items-start mb-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                <div className="ml-3">
                  <p className="text-sm font-medium text-green-800">Wohneinheit gefunden!</p>
                  <p className="text-sm text-green-700">
                    {searchResult.apartment.formattedNumber}
                  </p>
                </div>
              </div>
              <button
                onClick={handleSelectApartment}
                className="w-full py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
              >
                Diese Wohneinheit öffnen
              </button>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
            >
              Abbrechen
            </button>
          </div>
        </div>
      </div>

      {showScanner && (
        <BarcodeScanner
          onScan={handleScanComplete}
          onClose={() => setShowScanner(false)}
          title="Wohneinheit scannen"
          description="Scannen Sie den Barcode oder QR-Code der Wohneinheit"
        />
      )}
    </div>
  );
};